import { Badge, Box, Button, Flex, Grid, HStack, IconButton, Image, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { MdContentCopy, MdDelete, MdEdit } from 'react-icons/md';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { Filters, GetModelPaginatedRs, GetModelTypeRs, Model } from 'utils/interfaces';
import { deleteModel, getAllPaginated } from '../../../services/ModelService';
import CopyModelModal from './modal/CopyModelModal';
import DeleteConfirmationModal from './modal/DeleteConfirmationModal';
import UpdateModelModal from './modal/UpdateModelModal';
import { MdEmail } from "react-icons/md";
import NewCommunicationsModal from './modal/SendCommunicationsModal';

const ModelCardGrid = (
    { models, setModels, filters, setFilters, modelTypes, setLoading }:
        {
            models: GetModelPaginatedRs | undefined,
            setModels: (models: GetModelPaginatedRs | undefined) => void,
            filters: Filters<any>,
            setFilters: (filters: Filters<any>) => void,
            modelTypes: GetModelTypeRs[],
            setLoading: (loading: boolean) => void
        }) => {

    const { keycloak } = useKeycloak();

    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
    const { isOpen: isUpdateModalOpen, onOpen: onUpdateModalOpen, onClose: onUpdateModalClose } = useDisclosure();
    const { isOpen: isCopyModalOpen, onOpen: onCopyModalOpen, onClose: onCopyModalClose } = useDisclosure();
    const { isOpen: isSendCommunicationModalOpen, onOpen: onSendCommunicationModalOpen, onClose: onSendCommunicationModalClose } = useDisclosure();

    const [selectedModel, setSelectedModel] = useState<Model | null>(null);
    const [hoveredModel, setHoveredModel] = useState<Model | null>(null);

    const [currentPage, setCurrentPage] = useState<number>(0); // Stato per la pagina corrente
    const [totalPages, setTotalPages] = useState<number>(1); // Stato per il numero totale di pagine

    useEffect(() => {
        setLoading(true);
        fetchModels();
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, filters]);

    const onSessionExpired = () => {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
            position: "bottom-right",
            onClose: () => {
                keycloak.logout();
                localStorage.removeItem("kc-token");
            }
        });
    };

    const fetchModels = async () => {
        try {
            const modelsData = await getAllPaginated(
                keycloak.token,
                onSessionExpired,
                filters
            );

            setModels(modelsData);
            setTotalPages(modelsData.totalPages); // Aggiorna il numero totale di pagine
        } catch (err) {
            console.error(err);
            toast.error("Errore durante il recupero dei modelli", {
                position: "bottom-right"
            })
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage <= models.totalPages) {
            setCurrentPage(newPage);
            setFilters((prevFilters: any) => ({
                ...prevFilters,
                pageNumber: newPage,
            }));
        }
    };

    const onCloseUpdateModalWithFetch = () => {
        onUpdateModalClose();
        fetchModels();
    };

    const onCloseCopyModalWithFetch = () => {
        onCopyModalClose();
        fetchModels();
    };

    const confirmDelete = async () => {
        if (selectedModel) {
            try {
                await deleteModel(keycloak.token, onSessionExpired, selectedModel.id);
                await fetchModels();
                onDeleteModalClose();

                toast.success("Modello eliminato con successo", {
                    position: "bottom-right"
                });


            } catch (error) {
                toast.error("Errore durante l'eliminazione del modello", {
                    position: "bottom-right"
                });
            }
        }
    };

    return (
        <>
            <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4} width="100%" justifyContent="center">
                {models?.content?.map((model) => {
                    const coverSrc = model.coverImg ? `data:image/png;base64,${model.coverImg}` : null;
                    return (
                        <Box key={model.id}>
                            <Box
                                borderTopRadius="lg"
                                boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.18)"
                                overflow="hidden"
                                bg="white"
                                position="relative"
                                width="100%"
                                height="200px"
                                onMouseEnter={() => setHoveredModel(model)}
                                onMouseLeave={() => setHoveredModel(null)}
                            >
                                {coverSrc ? (
                                    <Image
                                        src={coverSrc}
                                        alt="Cover Image"
                                        width="100%"
                                    />
                                ) : (
                                    <Box
                                        height="200px"
                                        width="100%"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        bg="gray.100"
                                    >
                                        <Text color="gray.500" fontSize="lg">
                                            Copertina non presente
                                        </Text>
                                    </Box>
                                )}

                                <Badge
                                    position="absolute"
                                    top="10px"
                                    right="10px"
                                    background="#9FB50C"
                                    fontSize="0.8em"
                                    borderRadius="full"
                                    px={2}
                                    py={1}
                                >
                                    {model.modelType.type}
                                </Badge>

                                {hoveredModel?.id === model.id && (
                                    <Flex
                                        position="absolute"
                                        top="0"
                                        left="0"
                                        width="100%"
                                        height="100%"
                                        bg="rgba(0, 0, 0, 0.5)"
                                        alignItems="center"
                                        justifyContent="center"
                                        flexDirection="row"
                                        gap={2}
                                    >
                                        <IconButton
                                            icon={<MdEmail />}
                                            aria-label="Invia"
                                            size="md"
                                            onClick={() => {
                                                if (hoveredModel?.modelType?.type?.toLowerCase() !== "scadenze") {
                                                    setSelectedModel(model);
                                                    onSendCommunicationModalOpen();
                                                }
                                            }}
                                            isDisabled={hoveredModel?.modelType?.type?.toLowerCase() === "scadenze"} // Disabilita il pulsante per le scadenze
                                        />
                                        <Tooltip label="Modifica" hasArrow>
                                            <IconButton
                                                icon={<MdEdit />}
                                                aria-label="Modifica"
                                                size="md"
                                                onClick={() => {
                                                    setSelectedModel(model);
                                                    onUpdateModalOpen();
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip label="Copia" hasArrow>
                                            <IconButton
                                                icon={<MdContentCopy />}
                                                aria-label="Copia"
                                                size="md"
                                                onClick={() => {
                                                    setSelectedModel(model);
                                                    onCopyModalOpen();
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip label="Elimina" hasArrow>
                                            <IconButton
                                                icon={<MdDelete />}
                                                aria-label="Elimina"
                                                size="md"
                                                colorScheme="red"
                                                onClick={() => {
                                                    setSelectedModel(model);
                                                    onDeleteModalOpen();
                                                }}
                                            />
                                        </Tooltip>
                                    </Flex>
                                )}
                            </Box>
                            <Box
                                p={4}
                                borderBottomRadius="lg"
                                boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.18)"
                                overflow="hidden"
                                bg="white"
                            >
                                <Text fontWeight="bold" fontSize="lg" noOfLines={1} textAlign="center">
                                    {model.name}
                                </Text>
                            </Box>
                        </Box>
                    );
                })}
            </Grid>

            {/* Paginazione */}
            <HStack justifyContent="center" mt={10}>
                <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    isDisabled={currentPage === 0}
                >
                    Precedente
                </Button>
                <Text fontSize={{ base: "sm", md: "md" }} color="gray.500" textAlign="center">
                    Pagina {currentPage + 1} di {totalPages}
                </Text>
                <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    isDisabled={currentPage + 1 >= totalPages}
                >
                    Successiva
                </Button>
            </HStack>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={onDeleteModalClose}
                onConfirm={confirmDelete}
            />

            <UpdateModelModal
                selectedModel={selectedModel}
                isOpen={isUpdateModalOpen}
                onClose={onCloseUpdateModalWithFetch}
                modelTypes={modelTypes}
            />

            <CopyModelModal
                selectedModel={selectedModel}
                isOpen={isCopyModalOpen}
                onClose={onCloseCopyModalWithFetch}
                modelTypes={modelTypes}
            />

            <NewCommunicationsModal
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                isOpen={isSendCommunicationModalOpen}
                onClose={onSendCommunicationModalClose}
            />
        </>
    );
};

export default ModelCardGrid;